import {startTransition, useEffect, useRef, useState} from 'react';

import {openLandingUrl} from '@zzng/common/utils/openLandingUrl';

import {CardButton} from '@/root/models/card';
import {useUtilButtonsStore} from '@/root/stores/cardCarousel';
import {useCardGroupStore} from '@/root/stores/cardGroup';

import {TIARA_DETAIL_PAGE_NAME} from '@/shared/constants/tiara';
import useTiara from '@/shared/hooks/useTiara';

type ButtonState = {
  button: CardButton;
  exiting: boolean | null;
};

const CardUtilButtons = () => {
  const {groupCode} = useCardGroupStore();
  const {trackEvent} = useTiara({pageName: TIARA_DETAIL_PAGE_NAME.CARD_DETAIL});

  const newButtons = useUtilButtonsStore((state) => state.utilButtons);
  const [buttonsState, setButtonsState] = useState<ButtonState[]>(
    newButtons.map((button) => ({
      button,
      exiting: false
    }))
  );

  const containerRef = useRef<HTMLDivElement | null>(null);
  const buttonsRef = useRef<(HTMLButtonElement | null)[]>([]);

  const animateContainer = ({opacity, onFinish}: {opacity: string; onFinish?: () => void}) => {
    if (!containerRef.current) return;

    const currentOpacity = containerRef.current.style.opacity;
    if (currentOpacity === opacity) return;

    const keyframes = [{opacity: currentOpacity}, {opacity}];

    containerRef.current.getAnimations()?.forEach((animation) => {
      if (animation.playState === 'running') {
        animation.cancel();
      }
    });

    const animation = containerRef.current.animate(keyframes, {
      duration: 300,
      easing: 'ease-in-out',
      fill: 'forwards'
    });

    if (onFinish) {
      animation.onfinish = onFinish;
    }
  };

  const animateButton = ({
    buttonEl,
    isExiting,
    onFinish
  }: {
    buttonEl: HTMLButtonElement;
    isExiting: boolean;
    onFinish: () => void;
  }) => {
    buttonEl.getAnimations()?.forEach((animation) => {
      if (animation.playState === 'running') {
        animation.cancel();
      }
    });

    const buttonWidth = buttonEl.offsetWidth;

    const keyframes = isExiting
      ? [
          {opacity: 1, marginLeft: '22px'},
          {opacity: 0, marginLeft: `-${buttonWidth}px`}
        ]
      : [
          {opacity: 0, marginLeft: `-${buttonWidth}px`},
          {opacity: 1, marginLeft: '22px'}
        ];

    const animation = buttonEl.animate(keyframes, {
      duration: 300,
      easing: 'ease-in-out',
      fill: 'forwards'
    });

    animation.onfinish = onFinish;
  };

  const updateButtonsStates = () => {
    setButtonsState((prev) => {
      const prevSequences = prev.map((item) => item.button.sequence);
      const newSequences = newButtons.map((button) => button.sequence);

      // 기존에 있었지만 새 목록에 없는 버튼은 exit 처리
      const updatedButtonState = prev.map((item) => {
        if (!newSequences.includes(item.button.sequence)) {
          return {...item, exiting: true};
        }
        // 기존에 있던 버튼은 새로운 버튼으로 교체
        const newButton = newButtons.find((button) => button.sequence === item.button.sequence) as CardButton;
        return {button: newButton, exiting: null};
      });

      // 새로 추가된 버튼은 enter
      newButtons.forEach((button) => {
        if (!prevSequences.includes(button.sequence)) {
          updatedButtonState.push({button, exiting: false});
        }
      });

      return updatedButtonState;
    });
  };

  const onClickButton = (button: CardButton) => () => {
    openLandingUrl(button.landingUrl);
    trackEvent({
      action: `버튼${button.sequence + 1}_클릭`,
      customProps: {button_name: button.value, group_code: groupCode}
    });
  };

  useEffect(() => {
    startTransition(() => {
      // 버튼이 없는 경우
      if (!buttonsState.length && !newButtons.length) {
        return;
      }

      // 버튼이 있다가 모두 없어지는 경우
      if (buttonsState.length && !newButtons.length) {
        animateContainer({
          opacity: '0',
          onFinish: () => {
            setButtonsState([]);
          }
        });
        return;
      }

      // 버튼이 없다가 생기는 경우
      if (!buttonsState.length && newButtons.length) {
        setButtonsState(newButtons.map((button) => ({button, exiting: null})));
        return;
      }

      // 버튼이 추가되거나 삭제되는 경우 (1 -> 2 or (2 -> 1))
      updateButtonsStates();
    });
  }, [newButtons]);

  useEffect(() => {
    animateContainer({opacity: buttonsState.length ? '1' : '0'});

    buttonsRef.current.forEach((buttonEl, index) => {
      if (!buttonEl || index === 0) return; // 첫 번째 버튼 제외

      const isExit = buttonsState[index].exiting;
      if (isExit === null) return;
      animateButton({
        buttonEl,
        isExiting: isExit,
        onFinish: () => {
          if (isExit) {
            setButtonsState((prev) => prev.filter((button) => !button.exiting));
          }
        }
      });
    });
  }, [buttonsState]);

  return (
    <div
      className="area_util"
      ref={containerRef}
      style={{
        opacity: buttonsState.length === 0 ? '0' : undefined
      }}
    >
      {buttonsState
        // 버튼 순서대로 정렬
        .sort((a, b) => a.button.sequence - b.button.sequence)
        .map((item, index) => (
          <button
            ref={(el) => (buttonsRef.current[index] = el)}
            key={item.button.value}
            type="button"
            className="btn_util"
            onClick={onClickButton(item.button)}
          >
            <span className="icon_util">
              <img className="img_icon custom_icon" src={item.button.iconUrl} alt={item.button.value} />
            </span>
            <span className="txt_detail">{item.button.value}</span>
          </button>
        ))}
    </div>
  );
};

export default CardUtilButtons;
